<template>
  <div class="tab-content-inner">
    <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.search") }}</label>
        <b-form-input
          v-model="filter"
          class="search-input"
          :placeholder="$t('globals.type_to_search')"
        />
      </div>

      <div class="filter-group">
        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.type") }}</label>
          <b-form-select
            v-model="selectedEnum"
            :options="[{ text: $t('globals.all'), value: null }, ...getEnumTypeOptions]"
            class="filter-select"
          />
        </div>
      </div>

      <div class="action-buttons">
        <button
          class="reload-btn"
          @click="resetReload"
          :disabled="loading"
          v-b-tooltip.hover
          :title="$t('globals.clear_filters')"
        >
          <icon name="sync-alt" :spin="loading"></icon>
        </button>
        <b-button
          variant="success"
          class="create-btn"
          @click="openNewEnumModal"
          :disabled="!selectedEnum"
          style="background-color: #388e3c;"
        >
          {{ $t('globals.new').toUpperCase() }}
        </b-button>
      </div>
      
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in fields"
              :key="field.key"
              :class="{ sortable: field.sortable }"
              @click="field.sortable && handleSort(field.key)"
            >
              {{ field.label }}
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedItems"
            :key="item.id"
            class="table-row"
          >
            <td>{{ item.id }}</td>
            <td>{{ item.enum_type }}</td>
            <td>
              <span class="status-badge" :class="item.active === 'true' ? 'status-completed' : 'status-cancelled'">
                {{ item.active === 'true' ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.value }}</td>
            <td>{{ item.description || '-' }}</td>
            <td style="text-align: center;">
              <button
                v-if="item.active === 'true'"
                @click="toggleActive(item.id, false)"
                class="action-btn-disable"
              >
                {{ $t("globals.disable") }}
              </button>
              <button
                v-else
                @click="toggleActive(item.id, true)"
                class="action-btn-enable"
              >
                {{ $t("globals.enable") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t("globals.loading") }}...</p>
      </div>
      <div v-else-if="filteredItems.length === 0" class="empty-state">
        <p>{{ $t("globals.no_results") }}</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.per_page") }}</span>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          class="per-page-select"
        />
        <span class="summary-label" style="margin-left: 1rem;">
          {{ $t("globals.showing") }}
          {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, filteredItems.length) }}
          {{ $t("globals.of") }} {{ filteredItems.length }}
        </span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage = 1"
        >
          &lt;&lt;
        </button>
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          &lt;
        </button>
        <span style="padding: 0 1rem; color: #6c757d; font-size: 0.875rem;">
          {{ $t("globals.page") }} {{ currentPage }} {{ $t("globals.of") }} {{ totalPages }}
        </span>
        <button
          class="page-btn"
          :disabled="currentPage >= totalPages"
          @click="currentPage++"
        >
          &gt;
        </button>
        <button
          class="page-btn"
          :disabled="currentPage >= totalPages"
          @click="currentPage = totalPages"
        >
          &gt;&gt;
        </button>
      </div>
    </div>

    <ulu-create-enum-modal
      :show="showNewEnumModal"
      :close="closeModals"
      :enumType="selectedEnum"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import "vue-awesome/icons/sync-alt";

export default {
  components: {
    "ulu-create-enum-modal": () =>
      import("@/components/admin/enums/NewEnumModal"),
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      selectedEnum: null,
      showNewEnumModal: false,
      items: [],
      filter: "",
      currentPage: 1,
      perPage: 20,
      sortBy: "id",
      sortDesc: false,
      loading: false,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      fields: [
        {
          key: "id",
          label: this.$t("globals.id"),
          sortable: true
        },
        {
          key: "enum_type",
          label: this.$t("globals.type"),
          sortable: true
        },
        {
          key: "active",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "name",
          label: this.$t("globals.name"),
          sortable: true
        },
        {
          key: "value",
          label: this.$t("globals.value"),
          sortable: true
        },
        {
          key: "description",
          label: this.$t("globals.description"),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("globals.actions"),
          sortable: false
        },
      ]
    };
  },
  computed: {
    ...mapGetters("constants", ["getEnumTypeOptions"]),
    filteredItems() {
      if (!this.filter) {
        return this.sortedItems;
      }
      const searchTerm = this.filter.toLowerCase();
      return this.sortedItems.filter(item => {
        return (
          (item.id && item.id.toString().includes(searchTerm)) ||
          (item.enum_type && item.enum_type.toLowerCase().includes(searchTerm)) ||
          (item.name && item.name.toLowerCase().includes(searchTerm)) ||
          (item.value && item.value.toLowerCase().includes(searchTerm)) ||
          (item.description && item.description.toLowerCase().includes(searchTerm))
        );
      });
    },
    sortedItems() {
      const items = [...this.items];
      if (!this.sortBy) {
        return items;
      }

      return items.sort((a, b) => {
        const aVal = a[this.sortBy];
        const bVal = b[this.sortBy];

        if (aVal === bVal) return 0;

        let result = 0;
        if (aVal === null || aVal === undefined) {
          result = 1;
        } else if (bVal === null || bVal === undefined) {
          result = -1;
        } else {
          result = aVal < bVal ? -1 : 1;
        }

        return this.sortDesc ? -result : result;
      });
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage) || 1;
    }
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleFetch", "simpleMutation"]),
    openNewModal() {
      this.showNewEnumModal = true;
    },
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    fetchAllEnums() {
      const data = {
        query: `
          query {
            enumsAll {
              id
              enum_type
              active
              name
              value
              description
              _rowVariant
            }
          }
        `
      };

      this.loading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.items = res.enumsAll;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchEnumsByCategory(category) {
      const data = {
        query: `
          query {
            enumsAll(enum_type: "${category}") {
              id
              enum_type
              active
              name
              value
              description
              _rowVariant
            }
          }
        `
      };

      this.loading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.items = res.enumsAll;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggleActive(id, activeSet) {
      let data = {
        query: `
          mutation EnumEdit($enum: BillingEnumInput) {
            res: EnumEdit (enum: $enum)
          }
        `,
        variables: {
          enum: {
            id: id,
            active: activeSet
          }
        }
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.closeModals();
      });
    },
    closeModals() {
      this.showNewEnumModal = false;
      this.selectedEnum = null;
      this.fetchAllEnums();
    },
    resetReload() {
      this.filter = "";
      this.currentPage = 1;
      this.sortBy = "id";
      this.sortDesc = false;
      this.selectedEnum = null;
      this.fetchAllEnums();
    },
   
    openNewEnumModal() {
      this.showNewEnumModal = true;
    }
  },
  watch: {
    selectedEnum(new_val) {
      this.$emit('enum-type-changed', new_val);
      if (new_val) {
        this.fetchEnumsByCategory(new_val);
      } else {
        this.fetchAllEnums();
      }
    },
    filter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchAllEnums();
  }
};
</script>

<style scoped>
.action-btn-disable {
  background-color: #f57c00;
  color: white;
  border: none;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-btn-disable:hover {
  background-color: #e65100;
}

.action-btn-enable {
  background-color: #388e3c;
  color: white;
  border: none;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-btn-enable:hover {
  background-color: #2e7d32;
}
</style>
